import {combineLatest} from 'rxjs';
import {filter, map, startWith, throttleTime} from 'rxjs/operators';
import {calcTotalAssets} from '@/assets/calc';
import {currenciesCurrencyCache$} from '@/config';
import {fiat$} from '@/state/fiat';
import {indexPricesCache$} from '@/price-index/price-index';
import {lendingAccounts$} from '../accounts/accounts';
import type {Assets, AssetEntity, LendingAssetsOverview} from '@/types';

export const lendingAssets$ = lendingAccounts$.pipe(
  map((accounts) => accounts.map((account) => <AssetEntity>{currency: account.currency, valueEv: account.netEquityEv})),
  startWith([])
);

export const lendingTotalAssets$ = combineLatest([
  lendingAssets$,
  indexPricesCache$,
  currenciesCurrencyCache$,
  fiat$,
]).pipe(
  map(([lendingAssets, indexPricesCache, currencies, fiat]) =>
    calcTotalAssets(lendingAssets, indexPricesCache, currencies, fiat, true)
  ),
  filter<Assets>(Boolean)
);

export const lendingAssetsOverview$ = combineLatest([
  lendingAccounts$,
  indexPricesCache$,
  currenciesCurrencyCache$,
  fiat$,
]).pipe(
  throttleTime(100),
  map(([accounts, indexPricesCache, currencies, fiat]): LendingAssetsOverview => {
    const netEquityAssets = accounts.map(
      (account) => <AssetEntity>{currency: account.currency, valueEv: account.netEquityEv}
    );
    const totalEquityAssets = accounts.map(
      (account) => <AssetEntity>{currency: account.currency, valueEv: account.totalBalanceEv}
    );
    const totalDebtAssets = accounts.map(
      (account) => <AssetEntity>{currency: account.currency, valueEv: account.totalDebtEv}
    );
    const availableAssets = accounts.map(
      (account) => <AssetEntity>{currency: account.currency, valueEv: account.availableBalanceEv}
    );
    const collateralAssets = accounts.map(
      (account) => <AssetEntity>{currency: account.currency, valueEv: account.collateralBalanceEv}
    );
    const lockedWithdrawAssets = accounts.map(
      (account) => <AssetEntity>{currency: account.currency, valueEv: account.lockedWithdrawEv}
    );
    const supplyTotalInterestAssets = accounts.map(
      (account) => <AssetEntity>{currency: account.currency, valueEv: account.supplyTotalInterestEv}
    );
    const supplyTotalAssets = accounts.map(
      (account) => <AssetEntity>{currency: account.currency, valueEv: account.supplyAmountEv}
    );
    const defaultAsset = {
      btcEv: 0,
      btc: '--',
      usdEv: 0,
      usd: '--',
      money: '--',
      usdtEv: 0,
      usdt: '--',
    };
    return {
      netEquity: calcTotalAssets(netEquityAssets, indexPricesCache, currencies, fiat, true) || defaultAsset,
      totalEquity: calcTotalAssets(totalEquityAssets, indexPricesCache, currencies, fiat, true) || defaultAsset,
      totalDebt: calcTotalAssets(totalDebtAssets, indexPricesCache, currencies, fiat, true, false, 3) || defaultAsset,
      totalAvailable: calcTotalAssets(availableAssets, indexPricesCache, currencies, fiat, true) || defaultAsset,
      totalCollateralBalance:
        calcTotalAssets(collateralAssets, indexPricesCache, currencies, fiat, true) || defaultAsset,
      totalLockedWithdraw:
        calcTotalAssets(lockedWithdrawAssets, indexPricesCache, currencies, fiat, true) || defaultAsset,
      totalSupply: calcTotalAssets(supplyTotalAssets, indexPricesCache, currencies, fiat, true) || defaultAsset,
      supplyTotalInterest:
        calcTotalAssets(supplyTotalInterestAssets, indexPricesCache, currencies, fiat, true) || defaultAsset,
    };
  })
);
